"use client";

import { useState, useEffect } from "react";
import { Bot, ArrowRightLeft, TrendingUp, TrendingDown, Wallet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { $fetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface AgentTradingTabProps {
  symbol?: string;
}

interface AgentBalance {
  tokenBalance: string;
  otcBalance: string;
}

export default function AgentTradingTab({ symbol }: AgentTradingTabProps) {
  const { toast } = useToast();
  const [agentId, setAgentId] = useState("");
  const [balance, setBalance] = useState<AgentBalance | null>(null);
  const [isLoadingBalance, setIsLoadingBalance] = useState(false);
  const [tradeSide, setTradeSide] = useState<"buy" | "sell">("sell");
  const [amount, setAmount] = useState("");
  const [isTrading, setIsTrading] = useState(false);
  const [lastTrade, setLastTrade] = useState<any>(null);

  // Load agent ID from local storage or context
  useEffect(() => {
    const stored = localStorage.getItem("otc_active_agent_id");
    if (stored) setAgentId(stored);
  }, []);

  const fetchBalance = async () => {
    if (!agentId) return;
    setIsLoadingBalance(true);
    try {
      const { data } = await $fetch({
        url: `/api/agent/balance?agentId=${encodeURIComponent(agentId)}&token=session`,
        silentSuccess: true,
      });
      if (data) {
        setBalance({
          tokenBalance: data.tokenBalance || "0",
          otcBalance: data.otcBalance || "0",
        });
      }
    } catch (e) {
      console.error("Failed to fetch agent balance:", e);
    } finally {
      setIsLoadingBalance(false);
    }
  };

  useEffect(() => {
    fetchBalance();
  }, [agentId]);

  const handleTrade = async () => {
    if (!agentId || !amount || parseFloat(amount) <= 0) {
      toast({ title: "Invalid trade", variant: "destructive" });
      return;
    }

    setIsTrading(true);
    try {
      const response = await fetch("/api/agent/trade", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agentId,
          symbol: symbol || "COMPANY/USD",
          side: tradeSide,
          amount,
          price: "0",
          token: "session",
        }),
      });

      const result = await response.json();
      if (result.success) {
        toast({
          title: "Trade executed",
          description: result.message || `${tradeSide.toUpperCase()} ${amount} tokens`,
        });
        setLastTrade(result);
        setAmount("");
        fetchBalance();
      } else {
        throw new Error(result.error || "Trade failed");
      }
    } catch (error: any) {
      toast({
        title: "Trade failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsTrading(false);
    }
  };

  return (
    <div className="p-3 space-y-4">
      {/* Agent ID input */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block flex items-center gap-1">
          <Bot className="h-3 w-3" />
          Agent ID
        </label>
        <input
          type="text"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          placeholder="Enter agent ID..."
          className="w-full p-2 bg-muted rounded border text-sm"
        />
      </div>

      {/* Balance Display */}
      <div className="grid grid-cols-2 gap-2">
        <div className="p-2 rounded bg-blue-500/10 border border-blue-500/20">
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <Wallet className="h-3 w-3" />
            Delegated OTC
          </div>
          <p className="font-semibold text-sm">
            {isLoadingBalance ? "..." : balance ? parseFloat(balance.otcBalance).toFixed(4) : "0"}
          </p>
        </div>
        <div className="p-2 rounded bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-1 text-muted-foreground text-xs">
            <ArrowRightLeft className="h-3 w-3" />
            Token Balance
          </div>
          <p className="font-semibold text-sm">
            {isLoadingBalance ? "..." : balance ? parseFloat(balance.tokenBalance).toFixed(4) : "0"}
          </p>
        </div>
      </div>

      {/* Side selector */}
      <div className="flex gap-2">
        <button
          onClick={() => setTradeSide("sell")}
          className={cn(
            "flex-1 py-1.5 px-2 rounded border text-xs flex items-center justify-center gap-1",
            tradeSide === "sell"
              ? "bg-red-500/20 border-red-500 text-red-400"
              : "bg-muted border-border"
          )}
        >
          <TrendingDown className="h-3 w-3" />
          Sell Token → USDO
        </button>
        <button
          onClick={() => setTradeSide("buy")}
          className={cn(
            "flex-1 py-1.5 px-2 rounded border text-xs flex items-center justify-center gap-1",
            tradeSide === "buy"
              ? "bg-green-500/20 border-green-500 text-green-400"
              : "bg-muted border-border"
          )}
        >
          <TrendingUp className="h-3 w-3" />
          Buy Token ← USDO
        </button>
      </div>

      {/* Amount */}
      <div>
        <label className="text-xs text-muted-foreground mb-1 block">Amount</label>
        <div className="relative">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full p-2 bg-muted rounded border text-sm"
          />
          <button
            onClick={() => {
              if (balance?.otcBalance) setAmount(balance.otcBalance);
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-blue-500"
          >
            MAX
          </button>
        </div>
      </div>

      {/* Trade Button */}
      <button
        onClick={handleTrade}
        disabled={isTrading || !agentId || !amount}
        className={cn(
          "w-full py-3 rounded text-white font-semibold disabled:opacity-50",
          tradeSide === "sell"
            ? "bg-gradient-to-r from-red-500 to-orange-500"
            : "bg-gradient-to-r from-green-500 to-emerald-500"
        )}
      >
        {isTrading
          ? "Executing..."
          : tradeSide === "sell"
          ? `Sell ${amount || "0"} Tokens`
          : `Buy ${amount || "0"} Tokens`}
      </button>

      {/* Last Trade */}
      {lastTrade && (
        <div className="p-2 rounded bg-muted border text-xs space-y-1">
          <p className="font-medium text-green-400">Last Trade</p>
          <p>Order: {lastTrade.orderId}</p>
          <p>Tx: {lastTrade.txHash}</p>
          <p>{lastTrade.message}</p>
        </div>
      )}

      {/* Info */}
      <div className="p-2 rounded bg-blue-500/5 border border-blue-500/10">
        <p className="text-[10px] text-muted-foreground">
          Agent trades use the company&apos;s delegated 10% OTC escrow share.
          Sells claim from escrow then swap on AMM. Buys swap USDO from AMM.
        </p>
      </div>
    </div>
  );
}
